/**
 * ENGLISH registry of the dynamic excursion pages + FR↔EN slug join.
 *
 * EN content lives in slugs-en/ (one file per excursion, < 400 lines), typed
 * `ExcursionPage` exactly like FR → rendered by the shared ExcursionSlugView.
 * The FR structure stays the source (slug-excursions.ts); here we only add the
 * translation registry + the slug correspondence table.
 *
 * Slugs are proper-noun place names (Nice, Monaco, Verdon…) → mirrored from FR
 * one-to-one, which keeps canonicals/hreflang stable and avoids any collision.
 *
 * Consumed by: app/en/excursions/[slug]/page.tsx (generateStaticParams),
 * the metadata router and the sitemap extractor (excursionEnSlugs).
 */
import type { ExcursionPage } from "./slug-excursions";
import niceMonacoEn from "./slugs-en/nice-monaco";
import cannesGourdonEn from "./slugs-en/cannes-gourdon";
import antibesJuanEn from "./slugs-en/antibes-juan-les-pins";
import saintTropezEn from "./slugs-en/saint-tropez";
import rivieraItalienneEn from "./slugs-en/riviera-italienne";
import verdonEn from "./slugs-en/verdon";
import ezeLaTurbieEn from "./slugs-en/eze-la-turbie";
import saintPaulEn from "./slugs-en/saint-paul-de-vence";

/** Stable FR↔EN join: derives URLs, canonicals and hreflang on both sides. */
export const EXCURSION_SLUG_MAP: { fr: string; en: string }[] = [
  { fr: "nice-monaco", en: "nice-monaco" },
  { fr: "cannes-gourdon", en: "cannes-gourdon" },
  { fr: "antibes-juan-les-pins", en: "antibes-juan-les-pins" },
  { fr: "saint-tropez", en: "saint-tropez" },
  { fr: "riviera-italienne", en: "riviera-italienne" },
  { fr: "verdon", en: "verdon" },
  { fr: "eze-la-turbie", en: "eze-la-turbie" },
  { fr: "saint-paul-de-vence", en: "saint-paul-de-vence" },
];

export const excursionPagesEn: ExcursionPage[] = [
  niceMonacoEn,
  cannesGourdonEn,
  antibesJuanEn,
  saintTropezEn,
  rivieraItalienneEn,
  verdonEn,
  ezeLaTurbieEn,
  saintPaulEn,
];

/** EN page by EN slug (or undefined). */
export const getExcursionPageEn = (enSlug: string): ExcursionPage | undefined =>
  excursionPagesEn.find((p) => p.slug === enSlug);

/** EN slugs (generateStaticParams + EN sitemap). */
export const excursionEnSlugs = (): string[] =>
  EXCURSION_SLUG_MAP.map((m) => m.en);

export const frToEnExcursionSlug = (fr: string): string | undefined =>
  EXCURSION_SLUG_MAP.find((m) => m.fr === fr)?.en;

export const enToFrExcursionSlug = (en: string): string | undefined =>
  EXCURSION_SLUG_MAP.find((m) => m.en === en)?.fr;

/** FR canonical of an FR slug. */
export const excursionFrCanonical = (frSlug: string): string =>
  `/excursions/${frSlug}`;

/** EN canonical of an FR slug (undefined if there is no EN version). */
export const excursionEnCanonical = (frSlug: string): string | undefined => {
  const en = frToEnExcursionSlug(frSlug);
  return en ? `/en/excursions/${en}` : undefined;
};
